/**
 * 通用样式 - 排版部分
 *
 * 文字颜色、字号、字重、标题、统计数字等排版相关样式。
 * 由 common.ts 组合使用，不直接对外导出。
 */

import type { StyleDeps } from './style-types';

/**
 * 排版样式定义
 */
export function typographyStyles({ token: t, css }: StyleDeps) {
  return {
    // ==================== 文字样式 ====================

    /** 辅助文字样式 - 用于次要说明文字 */
    helpText: css`
      font-size: ${t.fontSizeSM}px;
      color: ${t.colorTextTertiary};
    `,

    /** 主色文字 */
    primaryText: css`
      color: ${t.colorPrimary};
    `,

    /** 次要文字 */
    secondaryText: css`
      color: ${t.colorTextSecondary};
    `,

    /** 三级文字（最淡） */
    tertiaryText: css`
      color: ${t.colorTextTertiary};
    `,

    /** 加粗文字 */
    boldText: css`
      font-weight: ${t.fontWeightStrong};
    `,

    /** 小字号文字 */
    smallText: css`
      font-size: ${t.fontSizeSM}px;
    `,

    /** 链接样式 */
    linkText: css`
      color: ${t.colorPrimary};
      cursor: pointer;
    `,

    /** 代码/等宽字体样式 */
    codeText: css`
      font-family: monospace;
      cursor: pointer;
      color: ${t.colorPrimary};
    `,

    // ==================== 标题样式 ====================

    /** 区块标题 */
    sectionTitle: css`
      font-size: ${t.fontSizeLG}px;
      font-weight: ${t.fontWeightStrong};
      margin-bottom: ${t.marginMD}px;
      color: ${t.colorPrimary};
      display: flex;
      align-items: center;
      gap: ${t.marginXS}px;
    `,

    /** 页面标题 */
    pageTitle: css`
      font-size: 16px;
      font-weight: 500;
    `,

    // ==================== 字体 ====================

    /** 字体大小 - 小 */
    fontSizeSm: css`
      font-size: ${t.fontSizeSM}px;
    `,

    /** 字体大小 - 基础 */
    fontSizeBase: css`
      font-size: ${t.fontSize}px;
    `,

    /** 字体大小 - 大 */
    fontSizeLg: css`
      font-size: ${t.fontSizeLG}px;
    `,

    // --- 固定字号工具类（不跟随 token，用于精确控制字号的场景） ---

    /** 字体大小 - 12px (固定值) */
    fontSize12: css`
      font-size: 12px;
    `,

    /** 字体大小 - 13px */
    fontSize13: css`
      font-size: 13px;
    `,

    /** 字体大小 - 14px */
    fontSize14: css`
      font-size: 14px;
    `,

    /** 字体大小 - 16px */
    fontSize16: css`
      font-size: 16px;
    `,

    /** 字体大小 - 20px */
    fontSize20: css`
      font-size: 20px;
    `,

    /** 字体大小 - 32px（大数字统计） */
    fontSize32: css`
      font-size: 32px;
    `,

    /** 字重 - 中等 */
    fontWeight500: css`
      font-weight: 500;
    `,

    /** 字重 - 粗体 */
    fontWeight600: css`
      font-weight: 600;
    `,

    /** 等宽字体 */
    fontMono: css`
      font-family: monospace;
    `,

    // ==================== 统计 ====================

    /** 统计数字 - 大 */
    statNumber: css`
      font-size: 32px;
      font-weight: 600;
      color: ${t.colorPrimary};
    `,

    /** 统计标签 */
    statLabel: css`
      font-size: 12px;
      color: ${t.colorTextSecondary};
    `,

    /** 统计卡片内边距 */
    statCardPadding: css`
      padding-top: 10px;
    `,
  };
}
